/**
 * <PatientChartLayout>
 *
 * Shared structural shell extracted from `<PatientPage>` and `<CallPage>`.
 *
 * Both pages render the same three structural primitives:
 *   1. <PatientChartActionBar>  (portaled to <body> — order in tree is cosmetic)
 *   2. <PatientChart>           (fixed sidebar pinned under the action bar)
 *   3. <ScrollContext.Provider> wrapping the chart-content scroll container
 *
 * They diverge on FIVE preserved behaviors (do NOT homogenize these):
 *   - sidebarWidth                       (PatientPage 280, CallPage 330)
 *   - scrollContainerWidthSubtract       (PatientPage 280, CallPage 344 —
 *                                          CallPage's chart sidebar is 330px
 *                                          wide visually but its scroll
 *                                          container subtracts 344px to leave
 *                                          a 14px gutter before the right pane)
 *   - scrollContainerId                  (PatientPage undefined, CallPage
 *                                          'call-scroll-container')
 *   - scrollContextScope                 (PatientPage 'row' so the chart
 *                                          sidebar inherits stickyTop=
 *                                          CHART_CONTENT_TOP_VAR; CallPage
 *                                          'scroll-container-only' so the
 *                                          sidebar keeps the default 64px
 *                                          stickyTop while the scroll
 *                                          container gets '0px')
 *   - rightPane                          (CallPage renders <EncounterWorkspace
 *                                          parentMode="page"> as a flex
 *                                          sibling; PatientPage omits it)
 *
 * They also differ on chart-pane swap mechanics: PatientPage swaps the chart
 * pane CONTENT inline when a new encounter is active (passes the swapped
 * content as `chartPaneContent`), while CallPage hides the chart pane via
 * `chartPaneStyle.display` and exposes the alternate UI in `rightPane`. Both
 * pages keep full control of their swap state by composing their own
 * `chartPaneContent` / `chartPaneStyle` / `rightPane` values.
 */
import { CSSProperties, ReactNode } from 'react'
import type { Patient } from '@medplum/fhirtypes'

import { ScrollContext } from '@/context/ScrollContext'
import { useDoseSpotChartViewSync } from '@/hooks/useDoseSpotPrescriptionSync'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'
import { PatientChart } from '@/pages/Patient/components/PatientChart'
import { PatientChartActionBar } from '@/pages/Patient/components/PatientChartActionBar'
import type { ChartItem } from '@/pages/Patient/components/PatientChart.utils'

type SectionCounts = Record<string, number | undefined> | undefined

type Props = {
  patient: Patient
  sectionCounts: SectionCounts
  currentPage: string
  onItemClick: (item: ChartItem) => void
  /**
   * Forwarded to `<PatientChart>` so `/call/:encounterId` highlights the
   * active encounter in chart-section headers. PatientPage omits it.
   */
  encounterId?: string
  /**
   * Optional `key` for `<PatientChart>` so callers can force-remount the chart
   * (CallPage uses `patientDetailsKey` to refresh chart counts after a
   * Progress Note finalize). PatientPage doesn't need this.
   */
  chartKey?: string
  /**
   * Extra className appended to `<PatientChart>`. CallPage uses `z-20 w-[330px]`
   * to stack the sidebar above the page chrome and pin its width to 330px.
   * PatientPage leaves it undefined.
   */
  chartExtraClassName?: string
  /**
   * Visual width of the chart sidebar (px). 280 for PatientPage, 330 for
   * CallPage. Also drives the chart content `marginLeft`.
   */
  sidebarWidth: number
  /**
   * Width subtracted from `100vw` in the chart-content scroll container's
   * width calc. Usually equals `sidebarWidth`, but CallPage uses 344px (a
   * 14px gutter past the 330px sidebar) — see file header.
   */
  scrollContainerWidthSubtract: number
  /**
   * DOM id assigned to the chart-content scroll container, AND set on
   * `ScrollContext.Provider.value.scrollContainerId` when present.
   * PatientPage omits it (no id, no scrollContainerId — page/window scroll).
   * CallPage passes `'call-scroll-container'`.
   */
  scrollContainerId?: string
  /**
   * `stickyTop` value placed on the `ScrollContext.Provider`. PatientPage
   * uses `CHART_CONTENT_TOP_VAR`; CallPage uses `'0px'`.
   */
  stickyTop: string
  /**
   * Where the `ScrollContext.Provider` wraps:
   *   - 'row'                      → wraps the entire chart row (sidebar
   *                                    inherits stickyTop). PatientPage.
   *   - 'scroll-container-only'    → wraps only the scroll container
   *                                    (sidebar keeps default 64px stickyTop).
   *                                    CallPage.
   */
  scrollContextScope: 'row' | 'scroll-container-only'
  /**
   * Optional right-edge inset forwarded to the portaled `<PatientChartActionBar>`.
   * PatientPage uses this to narrow the bar when overlays open. CallPage omits.
   */
  actionBarRightOffset?: number
  /**
   * Forwarded to `<PatientChartActionBar>` so a demographics edit re-reads the
   * patient into the parent's state (refreshing the header name/DOB).
   */
  onPatientEdited?: () => void
  /**
   * Outer wrapper className for the chart row. PatientPage uses
   * `'patientChart relative flex'`; CallPage leaves it undefined.
   */
  outerClassName?: string
  /**
   * Outer wrapper inline style. PatientPage sets `height: CHART_CONTENT_HEIGHT_VAR`;
   * CallPage omits.
   */
  outerStyle?: CSSProperties
  /**
   * Content rendered inside the chart-content scroll container. PatientPage
   * passes either `<EncounterWorkspace parentMode="side-panel">` (when a new
   * encounter is active) or the `currentItem.component` mount. CallPage
   * passes only the `currentItem.component` mount and hides the scroll
   * container via `chartPaneStyle.display` when the Progress Note pane is up.
   */
  chartPaneContent: ReactNode
  /**
   * Extra inline style merged into the chart-content scroll container.
   * Both callers use this for divergent extras:
   *   - PatientPage: `paddingRight` (chartRightOffset) + `transition`
   *   - CallPage:    `minHeight` + `display: 'none'` when swapped
   */
  chartPaneStyle?: CSSProperties
  /**
   * Extra className merged onto the chart-content scroll container.
   * CallPage uses `'relative overflow-x-hidden'`; PatientPage uses
   * `'box-border min-w-0 overflow-x-hidden overflow-y-auto'`.
   *
   * (Yes, the className sets diverge — Tailwind's overflow utilities are
   *  inlined here intentionally so each page can keep its existing class
   *  list verbatim. Refactor target: collapse once we confirm both pages
   *  can use the same overflow rules.)
   */
  chartPaneClassName: string
  /**
   * Inner wrapper rendered directly inside the chart-content scroll
   * container, around `chartPaneContent`. CallPage wraps content in
   * `<div className="w-full min-w-0">`; PatientPage skips it. When omitted,
   * `chartPaneContent` is rendered directly.
   */
  chartPaneInnerClassName?: string
  /**
   * Optional flex sibling rendered AFTER the chart-content scroll container,
   * inside the same `<SoapNotePlanProvider>` / flex wrapper. CallPage uses
   * this to mount `<EncounterWorkspace parentMode="page">`. PatientPage omits.
   *
   * When `rightPane` is set, the chart-content scroll container becomes a
   * flex sibling (the caller wraps both in their own flex container above
   * `<PatientChartLayout>`).
   */
  rightPane?: ReactNode
  /**
   * Optional extra content rendered AFTER the chart row (inside the outer
   * wrapper). PatientPage uses this for `<MessagesCollapsableContainer>`.
   */
  extraAfter?: ReactNode
  /**
   * Optional extra buttons appended to the `<PatientChartActionBar>` action
   * group. PatientPage uses this for the Messages toggle.
   */
  actionBarExtraActions?: ReactNode
  /**
   * Optional wrapper around the chart-pane scroll container + right pane.
   * CallPage wraps in `<SoapNotePlanProvider>` + flex layout. When omitted,
   * the scroll container renders directly. The wrapper receives the scroll
   * container (and optional right pane) as its children.
   */
  flexWrapper?: (children: ReactNode) => ReactNode
}

export function PatientChartLayout(props: Props) {
  const {
    patient,
    sectionCounts,
    currentPage,
    onItemClick,
    encounterId,
    chartKey,
    chartExtraClassName,
    sidebarWidth,
    scrollContainerWidthSubtract,
    scrollContainerId,
    stickyTop,
    scrollContextScope,
    actionBarRightOffset,
    onPatientEdited,
    outerClassName,
    outerStyle,
    chartPaneContent,
    chartPaneStyle,
    chartPaneClassName,
    chartPaneInnerClassName,
    rightPane,
    extraAfter,
    actionBarExtraActions,
    flexWrapper,
  } = props

  // The chart-view half of the DoseSpot write-back guarantee — "the chart is
  // correct whenever anyone looks at it". Mounted here rather than on either page
  // because this layout is what the chart and the live call share, so both views
  // get it from one call, exactly like the Prescribe control in the action bar.
  // Server-debounced to ≥5 minutes; a no-op for any patient never launched into
  // DoseSpot, and never blocking either way.
  useDoseSpotChartViewSync(patient)

  const scrollValue = {
    scrollContainerId,
    stickyTop,
    scrollToTop: () => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
  }

  // The PatientChart sidebar. Always renders the same shape — divergent
  // visual props (className, key, width) are wired via props.
  const chartSidebar = (
    <PatientChart
      key={chartKey}
      className={`fixed${chartExtraClassName ? ` ${chartExtraClassName}` : ''}`}
      style={{
        top: CHART_CONTENT_TOP_VAR,
        height: CHART_CONTENT_HEIGHT_VAR,
      }}
      currentPage={currentPage}
      patient={patient}
      sectionCounts={sectionCounts}
      encounterId={encounterId}
      onItemClick={onItemClick}
    />
  )

  // The chart-content scroll container. Width / margin computed from the
  // sidebar values. Caller-provided style + className merged on top of the
  // defaults.
  //
  // When a `flexWrapper` is provided, the caller's wrapper owns the
  // `ml-[sidebarWidth]px` (it wraps the chart pane + optional rightPane in a
  // flex container offset past the chart sidebar). In that mode we DROP
  // `marginLeft` from the scroll container itself — otherwise the inner
  // `marginLeft: ${sidebarWidth}px` compounds with the wrapper's
  // `ml-[sidebarWidth]` and the chart content starts at 2× the sidebar
  // width (e.g. x=560 instead of x=280), pushing the scroll container past
  // the viewport's right edge and clipping content via `overflow-x-hidden`.
  //
  // CallPage ALWAYS provides `flexWrapper` (so marginLeft is always dropped
  // there). PatientPage provides `flexWrapper` only when a new encounter is
  // active; in its non-encounter mode the wrapper is undefined and the
  // scroll container keeps its single `marginLeft: 280px`.
  const scrollContainer = (
    <div
      id={scrollContainerId}
      className={chartPaneClassName}
      style={{
        // Width / sidebar margin are owned by the layout (the divergent values
        // are wired via props). Height vs minHeight is left to the caller via
        // `chartPaneStyle` — PatientPage pins exact height; CallPage pins
        // minHeight so long content scrolls the page instead of an inner box.
        width: `calc(100vw - ${scrollContainerWidthSubtract}px - var(--zoom-panel-width, 0px))`,
        ...(flexWrapper ? {} : { marginLeft: `${sidebarWidth}px` }),
        ...chartPaneStyle,
      }}
    >
      {chartPaneInnerClassName ? (
        <div className={chartPaneInnerClassName}>{chartPaneContent}</div>
      ) : (
        chartPaneContent
      )}
    </div>
  )

  // When `rightPane` is provided (CallPage), compose scroll container + right
  // pane as flex siblings. Otherwise render the scroll container alone.
  const paneRow: ReactNode = rightPane ? (
    <>
      {scrollContainer}
      {rightPane}
    </>
  ) : (
    scrollContainer
  )

  // Optional caller wrapper (CallPage uses `<SoapNotePlanProvider>` + flex).
  const wrappedPanes = flexWrapper ? flexWrapper(paneRow) : paneRow

  // ScrollContext.Provider placement diverges by page — see file header.
  const rowBody =
    scrollContextScope === 'row' ? (
      <ScrollContext.Provider value={scrollValue}>
        <div className={outerClassName} style={outerStyle}>
          {chartSidebar}
          {wrappedPanes}
          {extraAfter}
        </div>
      </ScrollContext.Provider>
    ) : (
      <div className={outerClassName} style={outerStyle}>
        {chartSidebar}
        <ScrollContext.Provider value={scrollValue}>{wrappedPanes}</ScrollContext.Provider>
        {extraAfter}
      </div>
    )

  return (
    <>
      <PatientChartActionBar
        patient={patient}
        rightOffset={actionBarRightOffset}
        onPatientEdited={onPatientEdited}
        extraActions={actionBarExtraActions}
      />
      {rowBody}
    </>
  )
}
